import {
  streamLainMotionChannel,
  type StreamLainMotionScheduler,
  streamLainMotionScheduler,
} from '@blocksuite/affine-shared/utils';

/**
 * Edge auto-scroll for calendar drags. While a dragged pointer rests near or
 * beyond a visible edge of its scroll container, the container scrolls toward
 * that edge. Speed grows with how far the pointer reaches past the edge band
 * and eases toward that target, so scrolling starts and stops softly.
 */
export const CALENDAR_EDGE_AUTO_SCROLL = {
  /** Distance inside the visible edge where scrolling starts, in px. */
  bandPx: 32,
  /** Speed per px of reach into or past the edge band, in px/s. */
  speedPerPx: 14,
  maxSpeed: 1800,
  /** Time constant of the velocity easing, in ms. */
  easingMs: 90,
} as const;

/**
 * Signed scroll speed along one axis (negative toward `start`). `reach` is how
 * far the pointer is inside the edge band plus how far it is past the edge.
 */
export function calendarEdgeScrollSpeed(
  pointer: number,
  start: number,
  end: number
): number {
  const { bandPx, maxSpeed, speedPerPx } = CALENDAR_EDGE_AUTO_SCROLL;
  if (end - start <= bandPx * 2) return 0;
  const startReach = start + bandPx - pointer;
  if (startReach > 0) return -Math.min(maxSpeed, startReach * speedPerPx);
  const endReach = pointer - (end - bandPx);
  if (endReach > 0) return Math.min(maxSpeed, endReach * speedPerPx);
  return 0;
}

/** Eases a velocity toward its target over `deltaMs`, frame-rate independent. */
export function easeCalendarEdgeScrollVelocity(
  current: number,
  target: number,
  deltaMs: number
): number {
  const factor = 1 - Math.exp(-deltaMs / CALENDAR_EDGE_AUTO_SCROLL.easingMs);
  const next = current + (target - current) * factor;
  return Math.abs(next) < 1 && target === 0 ? 0 : next;
}

type Axis = 'x' | 'y';

function scrollsAlong(element: Element, axis: Axis): boolean {
  const style = getComputedStyle(element);
  const overflow = axis === 'y' ? style.overflowY : style.overflowX;
  if (!/(auto|scroll)/.test(overflow)) return false;
  return axis === 'y'
    ? element.scrollHeight > element.clientHeight + 1
    : element.scrollWidth > element.clientWidth + 1;
}

function nearestScroller(origin: Element, axis: Axis): HTMLElement | null {
  for (let el = origin.parentElement; el; el = el.parentElement) {
    if (scrollsAlong(el, axis)) return el;
  }
  return null;
}

/** Visible range of a scroller along one axis, minus a pinned header on top. */
function visibleRange(scroller: HTMLElement, axis: Axis): [number, number] {
  const rect = scroller.getBoundingClientRect();
  if (axis === 'x') return [rect.left, rect.right];
  const header = scroller.querySelector<HTMLElement>(
    '[data-calendar-sticky-header]'
  );
  const headerBottom = header?.getBoundingClientRect().bottom ?? rect.top;
  return [Math.max(rect.top, Math.min(headerBottom, rect.bottom)), rect.bottom];
}

export type CalendarEdgeAutoScroll = {
  /** Scroll distance applied since the drag started, per axis. */
  readonly scrolled: () => Readonly<{ x: number; y: number }>;
  stop: () => void;
  update: (
    pointer: Readonly<{ clientX: number; clientY: number }>,
    options?: Readonly<{ vertical?: boolean }>
  ) => void;
};

/**
 * Starts edge auto-scroll for a drag that began on `origin`. `onScroll` runs
 * after each scroll step so the drag can re-evaluate its target under the
 * resting pointer.
 */
export function startCalendarEdgeAutoScroll(
  origin: Element,
  onScroll: () => void,
  scheduler: StreamLainMotionScheduler = streamLainMotionScheduler
): CalendarEdgeAutoScroll {
  const channel = streamLainMotionChannel('calendar-edge-auto-scroll');
  const scrollers = {
    x: nearestScroller(origin, 'x'),
    y: nearestScroller(origin, 'y'),
  };
  const target = { x: 0, y: 0 };
  const velocity = { x: 0, y: 0 };
  const scrolled = { x: 0, y: 0 };
  let running = false;

  const step = (deltaMs: number): boolean => {
    let moved = false;
    for (const axis of ['x', 'y'] as const) {
      const scroller = scrollers[axis];
      velocity[axis] = easeCalendarEdgeScrollVelocity(
        velocity[axis],
        target[axis],
        deltaMs
      );
      if (!scroller || velocity[axis] === 0) continue;
      const before = axis === 'x' ? scroller.scrollLeft : scroller.scrollTop;
      const next = before + (velocity[axis] * deltaMs) / 1000;
      if (axis === 'x') scroller.scrollLeft = next;
      else scroller.scrollTop = next;
      const after = axis === 'x' ? scroller.scrollLeft : scroller.scrollTop;
      if (after === before) continue;
      scrolled[axis] += after - before;
      moved = true;
    }
    if (moved) onScroll();
    running =
      velocity.x !== 0 || velocity.y !== 0 || target.x !== 0 || target.y !== 0;
    return running;
  };

  return {
    scrolled: () => ({ ...scrolled }),
    stop: () => {
      target.x = target.y = velocity.x = velocity.y = 0;
      running = false;
      scheduler.cancel(channel);
    },
    update: (pointer, options) => {
      target.x = scrollers.x
        ? calendarEdgeScrollSpeed(
            pointer.clientX,
            ...visibleRange(scrollers.x, 'x')
          )
        : 0;
      target.y =
        scrollers.y && options?.vertical !== false
          ? calendarEdgeScrollSpeed(
              pointer.clientY,
              ...visibleRange(scrollers.y, 'y')
            )
          : 0;
      if (running || (target.x === 0 && target.y === 0)) return;
      running = true;
      scheduler.loop(channel, step);
    },
  };
}
